import { selectApiData } from 'util/selector/apiSelector';
import { createPaginationQuery, PaginationQuery } from 'util/createPaginationQuery';

import { WithData } from 'api/interfaces';
import { AxiosInstance } from 'axios';
import { LifecycleType } from 'model/Lifecycle';
import { mapProjectDtoToModel, ProjectDto, ProjectModel } from 'api/dto/Project.dto';
import { PaginationModel } from 'model/Pagination.dto';
import { ProjectKind } from 'model/ProjectKind';
import { ProjectStatus } from 'model/ProjectStatus';
import { Dayjs } from 'dayjs';
import { SortDirection } from 'shared/constants';

import {
  PulseSubProjectDto,
  PulseTemplateDto,
  PulseTemplateModel,
  mapPulseSubProjectDtoToModel,
  mapPulseTemplateDtoToModel,
} from './dto/PulseTemplate.dto';

export namespace PulseProjectApi {
  export type SubProjectsListEntry = {
    limit: number;
    page: number;
    pages: number;
    total: number;
    _embedded: {
      items: PulseSubProjectDto[];
    };
  };

  export type FetchSubProjectsQuery = PaginationQuery & {
    kind?: ProjectKind;
    status?: ProjectStatus[];
    order?: SortDirection;
  };

  export type PulseFrequencyData = {
    startDate: Dayjs;
    pulseFrequencyId: string;
  };

  export type ChangeFrequencyData = {
    pulseFrequencyId: string;
    startDate?: Dayjs;
  };

  export type ProjectCreatedFromTemplateDto = ProjectDto & {
    unmatchedLibraryReferences: string[];
  };

  export type ProjectCreatedFromTemplate = {
    project: ProjectModel;
    unmatchedLibraryReferences: string[];
  };

  export type CopySourceDto = {
    id: number;
    name: string;
    status: number;
    liveStart: string | null;
    liveStop: string | null;
  };

  export type CopySourceModel = {
    id: number;
    name: string;
    status: ProjectStatus;
    liveStart: string | null;
    liveStop: string | null;
  };

  export type CopySourcesListEntry = {
    limit: number;
    page: number;
    pages: number;
    total: number;
    _embedded: {
      items: CopySourceDto[];
    };
  };

  export type CopySourcesList = {
    list: CopySourceModel[];
    pagination: PaginationModel;
  };
}

const COPY_SOURCE_CREATED_STATUS = 4;

const mapCopySourceDtoToModel = ({
  id,
  name,
  status,
  liveStart,
  liveStop,
}: PulseProjectApi.CopySourceDto): PulseProjectApi.CopySourceModel => ({
  id,
  name,
  status: status === COPY_SOURCE_CREATED_STATUS ? ProjectStatus.InDevelopment : status,
  liveStart: liveStart ?? null,
  liveStop: liveStop ?? null,
});

export class PulseProjectApi {
  constructor(private apiClient: AxiosInstance) {}

  fetch(): Promise<PulseTemplateModel> {
    return this.apiClient
      .get<WithData<PulseTemplateDto>>('/api/v1/pulse-template/for-current-user')
      .then(selectApiData)
      .then(mapPulseTemplateDtoToModel);
  }

  get(pulseTemplateId: ProjectModel['id']): Promise<PulseTemplateModel> {
    return this.apiClient
      .get<WithData<PulseTemplateDto>>(`/api/v1/pulse-template/${pulseTemplateId}`)
      .then(selectApiData)
      .then(mapPulseTemplateDtoToModel);
  }

  create(
    pulseTemplateId: ProjectModel['id'],
    { startDate, pulseFrequencyId }: PulseProjectApi.PulseFrequencyData,
  ): Promise<PulseTemplateModel> {
    return this.apiClient
      .post<WithData<PulseTemplateDto>>(`api/v1/pulse-template/${pulseTemplateId}/create`, {
        startDate: startDate.toDate().toDateString(),
        pulseFrequencyId,
      })
      .then(selectApiData)
      .then(mapPulseTemplateDtoToModel);
  }

  createFromTemplate(
    pulseTemplateId: ProjectModel['id'],
    projectTemplateId: number,
  ): Promise<PulseProjectApi.ProjectCreatedFromTemplate> {
    return this.apiClient
      .post<WithData<PulseProjectApi.ProjectCreatedFromTemplateDto>>(
        `/api/v1/pulse-template/${pulseTemplateId}/pulse-project/from-template/${projectTemplateId}`,
        {},
      )
      .then(selectApiData)
      .then(({ unmatchedLibraryReferences = [], ...project }) => ({
        project: mapProjectDtoToModel(project),
        unmatchedLibraryReferences,
      }));
  }

  createFromPreviousSurvey(
    pulseTemplateId: ProjectModel['id'],
    sourceProjectId: ProjectModel['id'],
  ): Promise<ProjectModel> {
    const path = `/api/v1/pulse-template/${pulseTemplateId}/pulse-project/from-survey/${sourceProjectId}`;

    return this.apiClient
      .post<WithData<ProjectDto>>(path, {})
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  createEmpty(pulseTemplateId: ProjectModel['id']): Promise<ProjectModel> {
    return this.apiClient
      .post<WithData<ProjectDto>>(`/api/v1/pulse-template/${pulseTemplateId}/pulse-project`, {})
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  listCopySources(
    pulseTemplateId: ProjectModel['id'],
    { page, limit }: { page: number; limit: number },
  ): Promise<PulseProjectApi.CopySourcesList> {
    const query = createPaginationQuery({ page, limit });

    return this.apiClient
      .get<WithData<PulseProjectApi.CopySourcesListEntry>>(
        `/api/v1/pulse-template/${pulseTemplateId}/copy-sources${query}`,
      )
      .then(selectApiData)
      .then(({ page: currentPage, limit: pageLimit, pages, total, _embedded }) => ({
        list: (_embedded?.items || []).map(mapCopySourceDtoToModel),
        pagination: {
          page: currentPage,
          limit: pageLimit,
          pages,
          total,
        },
      }));
  }

  getChildProjects(pulseTemplateId: ProjectModel['id']): Promise<ProjectDto[]> {
    return this.apiClient
      .get(`/api/v1/pulse-template/${pulseTemplateId}/all-child-projects`)
      .then(selectApiData);
  }

  getSubProjects(
    pulseTemplateId: ProjectModel['id'],
    config: PulseProjectApi.FetchSubProjectsQuery,
  ): Promise<{
    list: ProjectModel[];
    pagination: PaginationModel;
  }> {
    const query = createPaginationQuery({
      page: config.page,
      limit: 9,
      sort: {
        liveStop: config.order,
      },
      filters: {
        kind: config.kind,
        status: config.status,
      },
    });

    return this.apiClient
      .get<WithData<PulseProjectApi.SubProjectsListEntry>>(
        `api/v1/pulse-template/${pulseTemplateId}/projects${query}`,
      )
      .then(selectApiData)
      .then(({ page, limit, pages, total, _embedded }) => {
        return {
          list: _embedded.items || [],
          pagination: {
            page,
            limit,
            pages,
            total,
          },
        };
      })
      .then(({ list, pagination }) => {
        return {
          list: list.map(mapPulseSubProjectDtoToModel),
          pagination,
        };
      });
  }

  updateFrequency(
    pulseTemplateId: ProjectModel['id'],
    { startDate, pulseFrequencyId }: PulseProjectApi.ChangeFrequencyData,
  ): Promise<PulseTemplateModel> {
    return this.apiClient
      .post<WithData<PulseTemplateDto>>(
        `api/v1/pulse-template/${pulseTemplateId}/change-frequency`,
        {
          ...(startDate && { startDate: startDate.toDate().toDateString() }),
          pulseFrequencyId,
        },
      )
      .then(selectApiData)
      .then(mapPulseTemplateDtoToModel);
  }

  distributeLifecycle(
    pulseTemplateId: ProjectModel['id'],
    lifecycleType: LifecycleType,
    employeesIds: number[],
  ): Promise<void> {
    return this.apiClient.post(
      `api/v1/pulse-template/${pulseTemplateId}/pulse-lifecycle-project/${lifecycleType}/distribute`,
      { parentRespondentIds: employeesIds },
    );
  }

  uploadRespondents(
    pulseTemplateId: ProjectModel['id'],
    lifecycleType: LifecycleType,
    file: File,
  ): Promise<void> {
    const data = new FormData();
    data.append('document', file);

    return this.apiClient.post(
      `api/v1/pulse-template/${pulseTemplateId}/pulse-lifecycle-project/${lifecycleType}/populate-respondents`,
      data,
    );
  }

  getAiContext(pulseProjectId: ProjectModel['id']): Promise<string> {
    return this.apiClient
      .get<WithData<{ pulseAIContext: string }>>(
        `/api/v1/pulse-project/${pulseProjectId}/ai-context`,
      )
      .then(selectApiData)
      .then((data) => data.pulseAIContext || '');
  }

  updateAiContext(pulseProjectId: ProjectModel['id'], pulseAIContext: string): Promise<void> {
    return this.apiClient.put(`/api/v1/pulse-project/${pulseProjectId}/ai-context`, {
      pulseAIContext,
    });
  }
}
